import { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Page, PageHeader } from '@/ui/Panel';
import { SubNav, SubNavItem } from '@/ui/Filters';
import { useAtLeast } from '@/lib/auth';
import { PromotionsTab } from '@/features/pricing/PromotionsTab';
import { CampaignsLocked, CampaignsTab } from '@/features/pricing/CampaignsTab';
import type { PricingSearch } from '@/features/pricing/search';
import { asText } from '@/lib/searchParams';

/**
 * Pricing — the two things that override a shelf price.
 *
 * **Promotions** are the register's rules: a sale price, a percentage, a BOGO,
 * scoped to a product or a department and fenced by dates and days of the week.
 *
 * **Campaigns** are pre-orders: a fixed list of products at a campaign price,
 * collected between an open date and an order cutoff. Manager-only, because
 * they commit the store to buying stock.
 *
 * They share a screen because they answer the same operator question — "why is
 * this not the shelf price?" — and because the alternative is two nav entries
 * for two features that are each one screen large.
 */

const TABS = ['promotions', 'campaigns'] as const;
const SORTS = ['name', 'starts_at', 'ends_at', 'cutoff_at', 'created_at'] as const;
const STATES = ['all', 'active', 'scheduled', 'paused', 'ended'] as const;

export const Route = createFileRoute('/_app/pricing')({
  staticData: { crumb: 'Pricing' },
  validateSearch: (s: Record<string, unknown>): PricingSearch => ({
    tab: TABS.includes(s.tab as PricingSearch['tab'])
      ? (s.tab as PricingSearch['tab'])
      : 'promotions',
    state: STATES.includes(s.state as PricingSearch['state'])
      ? (s.state as PricingSearch['state'])
      : 'all',
    q: asText(s.q, ''),
    promo: Number(s.promo) > 0 ? Number(s.promo) : undefined,
    id: Number(s.id) > 0 ? Number(s.id) : undefined,
    view: s.view === 'demand' ? 'demand' : 'items',
    active: s.active === 'true' ? 'true' : s.active === 'false' ? 'false' : 'all',
    sort: SORTS.includes(s.sort as PricingSearch['sort'])
      ? (s.sort as PricingSearch['sort'])
      : 'created_at',
    order: s.order === 'asc' ? 'asc' : 'desc',
    page: Number(s.page) > 0 ? Number(s.page) : 1,
  }),
  component: PricingScreen,
});

function PricingScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const isManager = useAtLeast('manager');

  const setSearch = useCallback(
    (patch: Partial<PricingSearch>) => {
      void navigate({
        search: (prev: PricingSearch) => ({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate]
  );

  const tab = search.tab === 'campaigns' && !isManager ? 'campaigns' : search.tab;

  return (
    <Page>
      <PageHeader
        eyebrow="Catalog"
        title="Pricing"
        subtitle={
          tab === 'promotions'
            ? 'Register price rules. Changes reach a lane on its next catalog sync, not instantly.'
            : 'Pre-order campaigns and the demand they have generated.'
        }
        tabs={
          <SubNav>
            <SubNavItem
              active={tab === 'promotions'}
              onClick={() => setSearch({ tab: 'promotions' })}
            >
              Promotions
            </SubNavItem>
            {/* Hidden entirely for non-managers — the capability is real, and a
                tab that always errors is worse than no tab. A deep link still
                lands on an explanation rather than a blank pane. */}
            {isManager ? (
              <SubNavItem
                active={tab === 'campaigns'}
                onClick={() => setSearch({ tab: 'campaigns' })}
              >
                Campaigns
              </SubNavItem>
            ) : null}
          </SubNav>
        }
      />

      {tab === 'promotions' ? (
        <PromotionsTab search={search} setSearch={setSearch} />
      ) : isManager ? (
        <CampaignsTab search={search} setSearch={setSearch} />
      ) : (
        <CampaignsLocked />
      )}
    </Page>
  );
}
